import React, { useEffect, useState } from 'react';
import { Button, Input } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined, KeyOutlined } from '@ant-design/icons';
import { createEhrConnection } from 'api/ehr';
import useEhrConnections from 'hooks/useEhrConnections';
import type { TEhrStepsConfig } from 'types/ehrConnection';
import { PF_SETUP_LABEL } from './ehrStatus';
import VerifyResultCard from './VerifyResultCard';
import s from './s.module.less';

// PF 的 Practice Access Code：10 位字母数字，区分大小写（与后端 normalisePac 同口径）
const PAC_PATTERN = /^[A-Za-z0-9]{10}$/;

// 同 AskChart askForCode：还没交 / PF 那边还没看到 agent / 缺 PAC / PAC 被拒时，进度说明下方常驻 PAC 输入框
const ASK_FOR_CODE = new Set([null, 'waiting_for_practice', 'access_code_needed', 'access_code_rejected']);

type Props = {
    ehrName: string;
    agentEmail: string;
    stepsConfig?: TEhrStepsConfig;
    // 结果卡的 Go back：回到引导页
    onBack: () => void;
    // setupStatus = connected：同其它 EHR 的验证成功
    onConnected: () => void;
    // 已有连接、后台开通中：宿主放开 Finish（不必等开通完）
    onReady: () => void;
};

// Practice Fusion（同 AskChart PracticeFusionVerify）：practice 把 agent 邮箱加进 PF 后交 PAC，
// 没有密码框——后端用 PAC 接受 PF 的邀请并自己设 agent 密码。开通在后台进行，不调 /verify，
// 进度只看连接列表里这一行的 setupStatus / setupMessage
export default function PracticeFusionVerify({
    ehrName,
    agentEmail,
    stepsConfig,
    onBack,
    onConnected,
    onReady,
}: Props) {
    const [connections, , refreshConnections] = useEhrConnections();
    const [code, setCode] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const pf = connections.find((c) => c.ehrSystem === ehrName);
    const status = pf ? (pf.setupStatus ?? null) : null;
    const message = pf?.setupMessage;

    // 进入即拉一次最新进度（已有连接的直接看到当前状态）
    useEffect(() => {
        refreshConnections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (status === 'connected') onConnected();
        else if (pf) onReady();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, !!pf]);

    const trimmed = code.replace(/\s+/g, '');
    const codeValid = PAC_PATTERN.test(trimmed);

    const handleSave = async () => {
        if (saving || !codeValid) return;
        setSaving(true);
        setError(null);
        const res = await createEhrConnection({ ehrSystem: ehrName, externalInfo: { PRACTICE_ACCESS_CODE: trimmed } });
        setSaving(false);
        if (!res || res.error) {
            setError("We couldn't save the code. Please check it and try again.");
            return;
        }
        setCode('');
        refreshConnections();
    };

    if (status === 'connected') {
        return (
            <div className={`${s.verifyState} ${s.verifyStateOk}`}>
                <div className={s.verifyStateDisc}>
                    <CheckCircleFilled />
                </div>
                <div className={s.verifyStateTitle}>Connection verified!</div>
                <div className={s.verifyStateDesc}>Klarity can now access your {ehrName} account.</div>
            </div>
        );
    }

    const header = (
        <>
            <h2 className={s.flowTitle}>Enter your Practice Access Code</h2>
            <p className={s.flowSubtitle}>
                After you add <strong>{agentEmail || 'the agent email'}</strong> as a user in {ehrName}, enter your
                Practice Access Code. We set up the agent&apos;s account from there, so you never need a password for
                it.
            </p>
        </>
    );

    // 保存失败：同验证失败页，Try again 回到输入卡（已填的码保留）
    if (error) {
        return (
            <>
                {header}
                <VerifyResultCard
                    tone="fail"
                    title="We couldn't save the code"
                    desc={error}
                    agentEmail={agentEmail}
                    stepsConfig={stepsConfig}
                    onBack={onBack}
                    onRetry={() => setError(null)}
                    retryLabel="Try again"
                />
            </>
        );
    }

    // 码已交上、后台开通中（setting_up）或需要人工支持（needs_support）：不再要码，只展示进度卡
    if (!ASK_FOR_CODE.has(status)) {
        return (
            <>
                {header}
                <VerifyResultCard
                    tone={status === 'needs_support' ? 'fail' : 'pending'}
                    title={status ? PF_SETUP_LABEL[status] : PF_SETUP_LABEL.setting_up}
                    desc={message}
                    agentEmail={agentEmail}
                    stepsConfig={stepsConfig}
                    onBack={onBack}
                />
            </>
        );
    }

    return (
        <>
            {header}
            {message && (
                <div className={status === 'access_code_rejected' ? s.pfStatusWarn : s.pfStatus}>
                    <ClockCircleOutlined />
                    <span>{message}</span>
                </div>
            )}
            <div className={s.verifyCard}>
                <div className={s.verifyIconBlue}>
                    <KeyOutlined style={{ fontSize: 26 }} />
                </div>
                <div className={s.verifySub}>
                    Find it in {ehrName} at the top left of Settings → Users, or on the Users tile of your dashboard. It
                    is 10 letters and numbers, case sensitive, and changes every week.
                </div>
                <div className={s.pfCodeWrap}>
                    <Input
                        size="large"
                        maxLength={12}
                        placeholder="Practice Access Code"
                        value={code}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(e) => {
                            setCode(e.target.value);
                            setError(null);
                        }}
                        onPressEnter={handleSave}
                    />
                    {code && !codeValid && <div className={s.pfCodeHint}>The code is 10 letters and numbers.</div>}
                </div>
                <div className={s.verifyActions}>
                    <Button type="primary" onClick={handleSave} disabled={!codeValid} loading={saving}>
                        Save code
                    </Button>
                </div>
            </div>
        </>
    );
}
